import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Trash2, Tag, Plus } from "lucide-react";

export const LEVELS = [
  { value: "none", label: "No access" },
  { value: "viewer", label: "Viewer" },
  { value: "editor", label: "Editor" },
  { value: "publisher", label: "Publisher" },
];

// Fetches named + auto groups once per mount; shared by the dashboard / dataset / admin pickers.
export function useAccessGroups() {
  const [groups, setGroups] = useState({ groups: [], auto_groups: [] });
  const reload = () => api.get("/access/groups").then(({ data }) => setGroups(data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  return { ...groups, all: [...groups.auto_groups, ...groups.groups], reload };
}

export function GroupSelect({ value, onChange, groups, exclude = [], testid = "group-select", placeholder = "Pick a group…" }) {
  const opts = groups.filter((g) => !exclude.includes(g.id));
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-8 text-xs bg-[color:var(--surface)]"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {opts.map((g) => (
          <SelectItem key={g.id} value={g.id} data-testid={`${testid}-opt-${g.id}`}>
            {g.name}{g.auto ? <span className="text-[color:var(--ink-3)]"> · auto</span> : ""}
          </SelectItem>
        ))}
        {!opts.length && <div className="px-2 py-1.5 text-xs text-[color:var(--ink-3)]">No groups available</div>}
      </SelectContent>
    </Select>
  );
}

export function LevelSelect({ value, onChange, testid, allowNone = true, className = "w-[118px]" }) {
  return (
    <Select value={value || "viewer"} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`h-7 text-xs bg-[color:var(--surface)] shrink-0 ${className}`}><SelectValue /></SelectTrigger>
      <SelectContent>{LEVELS.filter((l) => allowNone || l.value !== "none").map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

// Export / Share feature gates for one grant row. `gates` = {export?: bool, share?: bool}; undefined = allowed.
export function GateToggles({ gates, level, onChange, testid }) {
  if (!level || level === "none") return null;
  const g = gates || {};
  const set = (k, v) => onChange({ ...g, [k]: v });
  return (
    <span className="flex items-center gap-2 text-[10px] text-[color:var(--ink-3)]">
      <label className="flex items-center gap-1"><Checkbox data-testid={`${testid}-export`} checked={g.export !== false} onCheckedChange={(v) => set("export", !!v)} className="h-3.5 w-3.5" />Export</label>
      {level !== "viewer" && <label className="flex items-center gap-1"><Checkbox data-testid={`${testid}-share`} checked={g.share !== false} onCheckedChange={(v) => set("share", !!v)} className="h-3.5 w-3.5" />Share</label>}
    </span>
  );
}

// Dashboard-scoped group grants (named groups + auto role groups) — replaces the legacy role_grants UI.
export function DashboardGroupGrants({ dashboardId, grants, onChange }) {
  const { all } = useAccessGroups();
  const [gid, setGid] = useState("");
  const [level, setLevel] = useState("viewer");
  const save = async (group_id, lvl, gates) => {
    try {
      const { data } = await api.post(`/dashboards/${dashboardId}/group-grants`, { group_id, level: lvl, gates: gates || null });
      onChange(data.group_grants || []); toast.success("Group access updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (group_id) => {
    try { const { data } = await api.delete(`/dashboards/${dashboardId}/group-grants/${group_id}`); onChange(data.group_grants || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="group-grants-section" className="mt-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-2">
      <p className="text-xs font-semibold text-[color:var(--ink)] flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Groups</p>
      <p className="text-[11px] text-[color:var(--ink-3)]">Named groups from Team, plus auto groups for every workspace role. Direct user grants take precedence over group grants.</p>
      <div className="flex gap-2">
        <div className="flex-1 min-w-0"><GroupSelect value={gid} onChange={setGid} groups={all} exclude={grants.map((g) => g.group_id)} testid="group-grant-pick" /></div>
        <LevelSelect value={level} onChange={setLevel} testid="group-grant-level" className="w-[112px]" />
        <Button data-testid="group-grant-add" size="sm" className="h-8 bg-[color:var(--brand-ocean)] text-white shrink-0" disabled={!gid} onClick={() => { save(gid, level); setGid(""); }}><Plus className="w-4 h-4" /></Button>
      </div>
      {grants.map((g) => (
        <div key={g.group_id} data-testid="group-grant-row" className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)]">
          <span className="text-xs text-[color:var(--ink-2)] truncate flex items-center gap-1.5">{g.group_name || g.group_id}{g.group_id.startsWith("role:") && <Badge variant="outline" className="text-[9px] px-1 py-0">auto</Badge>}</span>
          <span className="flex items-center gap-2 shrink-0">
            <GateToggles gates={g.gates} level={g.level} testid={`group-grant-gate-${g.group_id}`} onChange={(gates) => save(g.group_id, g.level, gates)} />
            <LevelSelect value={g.level} onChange={(v) => save(g.group_id, v, g.gates)} testid={`group-grant-row-level-${g.group_id}`} className="w-[104px]" />
            <button data-testid={`group-grant-remove-${g.group_id}`} onClick={() => remove(g.group_id)} className="text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
          </span>
        </div>
      ))}
      {!grants.length && <p className="text-[11px] text-[color:var(--ink-3)]">No group grants yet.</p>}
    </div>
  );
}

// Free-text dashboard category; drives "Dashboards in category" grants in Admin Console → Permissions.
export function DashboardCategoryField({ dashboardId, category, onChange, compact = false }) {
  const [val, setVal] = useState(category || "");
  const [cats, setCats] = useState([]);
  useEffect(() => { setVal(category || ""); }, [category]);
  useEffect(() => { api.get("/access/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {}); }, []);
  const save = async () => {
    try { const { data } = await api.post(`/dashboards/${dashboardId}/category`, { category: val.trim() }); onChange(data.category); toast.success(data.category ? `Category set to “${data.category}”` : "Category cleared"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="dashboard-category-section" className={compact ? "space-y-1.5" : "mt-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-1.5"}>
      <p className="text-xs font-semibold text-[color:var(--ink)] flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />Category</p>
      <div className="flex gap-2">
        <Input data-testid="dashboard-category-input" list="dashboard-category-options" value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. Finance" className="h-8 text-xs bg-[color:var(--surface)]" maxLength={60} />
        <datalist id="dashboard-category-options">{cats.map((c) => <option key={c} value={c} />)}</datalist>
        <Button data-testid="dashboard-category-save" size="sm" variant="outline" className="h-8 text-xs shrink-0" disabled={(val || "").trim() === (category || "")} onClick={save}>Save</Button>
      </div>
      <p className="text-[11px] text-[color:var(--ink-3)]">Admins can grant access to every dashboard in a category (Admin Console → Permissions).</p>
    </div>
  );
}
